import { Award, BarChart2, DollarSign, Download, TrendingUp, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import collaborationService from '../../services/collaborationService';
import influencerService from '../../services/influencerService';
import LoadingSpinner from '../ui/LoadingSpinner';

const StatCard = ({ label, value, sub, icon: Icon, color = 'text-foreground' }) => (
  <div className="bg-card rounded-lg shadow p-5">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm text-muted-foreground mb-1">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground/70 mt-1">{sub}</p>}
      </div>
      {Icon && <Icon size={24} className="text-muted-foreground/50" />}
    </div>
  </div>
);

const computeScore = (row) => {
  const reach = Math.min(35, Math.log10(Math.max(row.follower_count || 1, 1)) * 5.8);
  const revenue = Math.min(30, row.totalRevenue / 50);
  const collab = Math.min(20, row.collabs * 5);
  const content = Math.min(5, row.contentPieces * 2);
  const engagement = Math.min(10, (parseFloat(row.engagement_rate) || 0) * 2);
  return Math.round(reach + revenue + collab + content + engagement);
};

const ScoreBadge = ({ score, row }) => {
  const reach = Math.min(35, Math.log10(Math.max(row?.follower_count || 1, 1)) * 5.8).toFixed(1);
  const revenue = Math.min(30, (row?.totalRevenue || 0) / 50).toFixed(1);
  const collab = Math.min(20, (row?.collabs || 0) * 5).toFixed(0);
  const content = Math.min(5, (row?.contentPieces || 0) * 2).toFixed(0);
  const engagement = Math.min(10, (parseFloat(row?.engagement_rate) || 0) * 2).toFixed(1);
  const tooltip = `Reach: ${reach}/35 · Revenue: ${revenue}/30 · Collabs: ${collab}/20 · Content: ${content}/5 · Engagement: ${engagement}/10`;

  const color =
    score >= 80
      ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
      : score >= 60
        ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
        : score >= 35
          ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
          : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${color} cursor-help`} title={tooltip}>
      {score}
    </span>
  );
};

export default function InsightsTab() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [influencers, setInfluencers] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [content, setContent] = useState([]);
  const [sortBy, setSortBy] = useState('score');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [infData, collabData, contentData] = await Promise.all([
          influencerService.getInfluencers({ limit: 500 }).then((r) => r.items),
          collaborationService.getCollaborations(),
          collaborationService.getContent(),
        ]);
        setInfluencers(infData);
        setCollabs(collabData);
        setContent(contentData);
      } catch {
        showToast('Failed to load insights', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply date filter to collaborations
  const filteredCollabs = useMemo(() => {
    return collabs.filter((c) => {
      if (dateFrom && new Date(c.created_at) < new Date(dateFrom)) return false;
      if (dateTo && new Date(c.created_at) > new Date(`${dateTo}T23:59:59`)) return false;
      return true;
    });
  }, [collabs, dateFrom, dateTo]);

  // Filter content to only include pieces from collaborations in the date range
  const filteredContent = useMemo(() => {
    if (!dateFrom && !dateTo) return content;
    const collabIds = new Set(filteredCollabs.map((c) => c.id));
    return content.filter((c) => collabIds.has(c.collaboration_id));
  }, [content, filteredCollabs, dateFrom, dateTo]);

  // Memoized summary stats
  const { spendByCurrency, hasMultipleCurrencies, singleSpend, totalRevenue, totalFollowers, totalReach, roi } =
    useMemo(() => {
      const spend = {};
      filteredCollabs
        .filter((c) => ['payment_done', 'campaign_complete'].includes(c.status))
        .forEach((c) => {
          const curr = c.agreed_currency || 'USD';
          spend[curr] = (spend[curr] || 0) + parseFloat(c.agreed_rate || 0);
        });
      const hasMulti = Object.keys(spend).length > 1;
      const single = !hasMulti ? Object.values(spend)[0] || 0 : 0;
      const rev = filteredContent.reduce((sum, c) => sum + parseFloat(c.revenue_attributed || 0), 0);
      const followers = filteredContent.reduce((sum, c) => sum + (c.followers_gained || 0), 0);
      const reach = filteredContent.reduce((sum, c) => sum + (c.reach || 0), 0);
      const roiVal = !hasMulti && single > 0 ? (rev / single).toFixed(1) : null;
      return {
        spendByCurrency: spend,
        hasMultipleCurrencies: hasMulti,
        singleSpend: single,
        totalRevenue: rev,
        totalFollowers: followers,
        totalReach: reach,
        roi: roiVal,
      };
    }, [filteredCollabs, filteredContent]);

  const spendDisplay =
    Object.entries(spendByCurrency)
      .map(([curr, amt]) => `${curr} ${amt.toFixed(0)}`)
      .join(' + ') || '—';

  // Per-influencer rollup (memoized)
  const rows = useMemo(() => {
    const influencerMap = {};
    influencers.forEach((inf) => {
      influencerMap[inf.id] = {
        id: inf.id,
        name: inf.name,
        ig_username: inf.ig_username,
        follower_count: inf.follower_count,
        engagement_rate: inf.engagement_rate,
        collabs: 0,
        totalPaid: 0,
        totalPaidByCurrency: {},
        contentPieces: 0,
        totalRevenue: 0,
        totalReach: 0,
      };
    });

    filteredCollabs.forEach((c) => {
      if (!influencerMap[c.influencer_id]) return;
      influencerMap[c.influencer_id].collabs += 1;
      if (['payment_done', 'campaign_complete'].includes(c.status)) {
        const amt = parseFloat(c.agreed_rate || 0);
        const curr = c.agreed_currency || 'USD';
        influencerMap[c.influencer_id].totalPaid += amt;
        influencerMap[c.influencer_id].totalPaidByCurrency[curr] =
          (influencerMap[c.influencer_id].totalPaidByCurrency[curr] || 0) + amt;
      }
    });

    filteredContent.forEach((c) => {
      if (!influencerMap[c.influencer_id]) return;
      influencerMap[c.influencer_id].contentPieces += 1;
      influencerMap[c.influencer_id].totalRevenue += parseFloat(c.revenue_attributed || 0);
      influencerMap[c.influencer_id].totalReach += c.reach || 0;
    });

    return Object.values(influencerMap)
      .filter((r) => r.collabs > 0)
      .map((r) => ({ ...r, score: computeScore(r) }));
  }, [influencers, filteredCollabs, filteredContent]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        if (sortBy === 'score') return b.score - a.score;
        if (sortBy === 'revenue') return b.totalRevenue - a.totalRevenue;
        if (sortBy === 'paid') return b.totalPaid - a.totalPaid;
        if (sortBy === 'reach') return b.totalReach - a.totalReach;
        if (sortBy === 'content') return b.contentPieces - a.contentPieces;
        return b.collabs - a.collabs;
      }),
    [rows, sortBy],
  );

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );

  const exportCSV = () => {
    const headers = [
      'Influencer',
      'Username',
      'Score',
      'Collabs',
      'Content Pieces',
      'Spend',
      'Revenue (USD)',
      'ROI',
      'Reach',
    ];
    const rowData = sortedRows.map((row) => {
      const spendStr =
        Object.entries(row.totalPaidByCurrency)
          .map(([c, a]) => `${c} ${a.toFixed(0)}`)
          .join(' + ') || '';
      const rowRoi = row.totalPaid > 0 ? (row.totalRevenue / row.totalPaid).toFixed(1) : '';
      return [
        row.name,
        `@${row.ig_username}`,
        row.score,
        row.collabs,
        row.contentPieces,
        spendStr,
        row.totalRevenue > 0 ? row.totalRevenue.toFixed(0) : '',
        rowRoi,
        row.totalReach > 0 ? row.totalReach : '',
      ];
    });
    const csv = [headers, ...rowData].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `influencer-insights-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Influencer Insights</h2>
          <p className="text-muted-foreground text-sm mt-1">Aggregate performance across all collaborations</p>
        </div>
        {/* Date filter */}
        <div className="flex items-center gap-2 text-sm">
          <label className="text-muted-foreground whitespace-nowrap">Collab date:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-1.5 border border-input rounded-lg text-sm"
          />
          <span className="text-muted-foreground/70">–</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-1.5 border border-input rounded-lg text-sm"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              className="text-xs text-muted-foreground/70 hover:text-foreground underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Spend"
          value={spendDisplay}
          icon={DollarSign}
          sub={hasMultipleCurrencies ? 'multiple currencies' : 'paid collaborations'}
        />
        <StatCard
          label="Revenue Attributed"
          value={totalRevenue > 0 ? `$${totalRevenue.toFixed(0)}` : '—'}
          icon={TrendingUp}
          color={totalRevenue > 0 ? 'text-green-700 dark:text-green-300' : 'text-foreground'}
          sub="from content"
        />
        <StatCard
          label="Overall ROI"
          value={roi ? `${roi}x` : '—'}
          icon={BarChart2}
          color={roi && parseFloat(roi) >= 1 ? 'text-green-700 dark:text-green-300' : 'text-foreground'}
          sub={
            hasMultipleCurrencies ? 'N/A (mixed currencies)' : singleSpend > 0 ? 'revenue / spend' : 'no spend recorded'
          }
        />
        <StatCard
          label="Total Reach"
          value={totalReach > 0 ? `${(totalReach / 1000).toFixed(1)}K` : '—'}
          icon={Users}
          sub={`${totalFollowers} followers gained`}
        />
      </div>

      {/* Per-influencer table */}
      {sortedRows.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-10 text-center text-muted-foreground/70">
          No collaboration data yet. Start creating collaborations to see insights here.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <div className="px-5 py-3 border-b flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Award size={16} className="text-muted-foreground/70" />
              <span className="text-sm font-medium text-foreground/80">Influencer Performance</span>
              <span className="text-xs text-muted-foreground/70">· Hover score for breakdown</span>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs border border-input rounded-md px-2 py-1"
              >
                <option value="score">Sort: Score</option>
                <option value="revenue">Sort: Revenue</option>
                <option value="paid">Sort: Spend</option>
                <option value="reach">Sort: Reach</option>
                <option value="content">Sort: Content pieces</option>
                <option value="collabs">Sort: Collaborations</option>
              </select>
              <button
                onClick={exportCSV}
                className="flex items-center gap-1.5 px-3 py-1 text-xs border border-input rounded-lg hover:bg-muted/50"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  {['Influencer', 'Score', 'Collabs', 'Content', 'Spend', 'Revenue', 'ROI', 'Reach'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {sortedRows.map((row, i) => {
                  const rowRoi = row.totalPaid > 0 ? (row.totalRevenue / row.totalPaid).toFixed(1) : null;
                  const spendStr =
                    Object.entries(row.totalPaidByCurrency)
                      .map(([c, a]) => `${c} ${a.toFixed(0)}`)
                      .join(' + ') || '—';
                  return (
                    <tr key={row.id} className="hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {i < 3 && <span className="text-yellow-400 text-sm">{'★'.repeat(3 - i)}</span>}
                          <div>
                            <p className="text-sm font-medium text-foreground">{row.name}</p>
                            <p className="text-xs text-muted-foreground/70">@{row.ig_username}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <ScoreBadge score={row.score} row={row} />
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{row.collabs}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{row.contentPieces}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{spendStr}</td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={
                            row.totalRevenue > 0
                              ? 'text-green-700 dark:text-green-300 font-medium'
                              : 'text-muted-foreground/70'
                          }
                        >
                          {row.totalRevenue > 0 ? `$${row.totalRevenue.toFixed(0)}` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {rowRoi ? (
                          <span
                            className={
                              parseFloat(rowRoi) >= 1
                                ? 'text-green-700 dark:text-green-300 font-medium'
                                : 'text-red-600 dark:text-red-400'
                            }
                          >
                            {rowRoi}x
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">
                        {row.totalReach > 0 ? `${(row.totalReach / 1000).toFixed(1)}K` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
